import { z } from "zod";

/**
 * How much of a subagent's report crosses the wire.
 *
 * Sized to stay well inside the host's read buffer while carrying any report a
 * model realistically writes. The number matters less than the bound existing:
 * the field it guards duplicates prose the same line already carries, and the
 * transport discards an oversized line rather than its excess.
 */
export const AGENT_DISPATCH_REPORT_MAX_CHARS = 262_144;

/**
 * The structured result of an Agent dispatch, projected from the SDK's
 * `tool_use_result` on the `user` message that settles the call.
 *
 * The SDK hands a tool's full Output object beside the string the model was
 * shown, and for an Agent dispatch that object is the subagent's own report
 * without the model-directed `agentId`/usage trailer, plus the run's totals.
 * Reading it is what lets a consumer act on the run rather than parse prose out
 * of the text — the corpus's own guidance, and the reason this slice exists.
 *
 * **Two arms, because two are what this transport receives.** Measured across
 * the capture corpus: 18 `completed` and 95 `async_launched`. The vendor
 * declares a third, `remote_launched`, for cloud-hosted runs; the corpus never
 * provoked one, so its zero is an unarmed probe rather than a negative and it is
 * deliberately not a member here. Typing it would buy a wire contract, a reader
 * on both hops and an exhaustiveness arm for traffic that never arrives. The
 * runner never puts one on the wire — it reports
 * nothing for an unrecognised arm — so the tool result simply travels without
 * this field. Were one ever emitted, the host's strict parse would refuse the
 * whole line rather than the field.
 *
 * **This is a projection, not the vendor's object, and the runner projects
 * before it emits.** The shapes on both wire hops are strict, so a field the
 * reading side has not been told about costs the whole frame rather than the
 * field — and the frame here is the tool result, which settles the call.
 * Mirroring the vendor's nested `usage` (whose own `iterations` the vendor
 * declares `unknown`) would hand the next SDK release a way to make a tool call
 * go unsettled. So the runner reads the vendor's object, builds the shape below,
 * and puts only that on the wire: the run's totals as flat numbers, the report
 * as the text the subagent wrote. That ordering is also what lets these arms be
 * strict, which guards the reading side rather than the writing one: the runner
 * projects a fixed key set, so nothing surplus is ever offered here, and
 * strictness earns its keep at the host against a drifted runner.
 *
 * What keeps another tool out is `agentId` being required. `status` does not do
 * it — the SDK's Workflow tool declares the same `async_launched` literal and
 * settles with a `taskId`. Relaxing `agentId` would open that gate with no
 * strictness to catch it.
 *
 * **Optionality follows the vendor's declaration, not the corpus's counts.**
 * `agentType` and `resolvedModel` were present in every capture and are optional
 * here because the SDK declares them so: a required field the SDK omits once
 * would have the whole tool result refused.
 *
 * **A failed dispatch is not a member.** Two captured Agent results arrived as a
 * bare string rather than an object, `is_error` set on the block — a shape
 * `AgentOutput` does not declare an arm for. Such a payload fails this schema,
 * the field is left off, and the failure travels where it already did: in the
 * tool result's own `output` and `isError`.
 */
const AgentDispatchCompletedSchema = z
  .object({
    status: z.literal("completed"),
    agentId: z.string().min(1),
    agentType: z.string().min(1).optional(),
    /**
     * The subagent's report — the text blocks of the SDK's `content` array joined,
     * which is the answer without the trailer the SDK appends for the model.
     *
     * Not `.min(1)`: a completed dispatch was captured with an empty `content`
     * array, and a subagent that finished having written nothing is a real outcome
     * to report rather than a frame to refuse.
     *
     * Capped, because this is the second unbounded prose field on a line the
     * transport drops WHOLE rather than truncating. The tool result already
     * carries this answer in `output`, so an Agent dispatch's line roughly
     * doubles — and past the host's read buffer the line is discarded, losing
     * the frame that settles the call: the tool card never settles and no result
     * is persisted. The runner truncates to this length before validating, so
     * the cap degrades a long report rather than dropping a dispatch.
     */
    report: z.string().max(AGENT_DISPATCH_REPORT_MAX_CHARS),
    resolvedModel: z.string().min(1).optional(),
    totalDurationMs: z.number().nonnegative(),
    totalTokens: z.number().nonnegative(),
    totalToolUseCount: z.number().nonnegative()
  })
  .strict();

/**
 * A dispatch the lead sent to the background. It carries a handle rather than an
 * answer: the run has not happened yet, so there are no totals, and the text the
 * model was shown is the harness instructing the model — not something to render.
 *
 * The SDK's `outputFile` and `prompt` are deliberately not carried. The first is
 * a path inside the sandbox, which resolves for no reader on this side of the
 * wire; the second is the model-authored input, already on the wire as the
 * dispatch's own tool call.
 */
const AgentDispatchAsyncLaunchedSchema = z
  .object({
    status: z.literal("async_launched"),
    agentId: z.string().min(1),
    description: z.string(),
    resolvedModel: z.string().min(1).optional()
  })
  .strict();

export const AgentDispatchResultSchema = z.discriminatedUnion("status", [
  AgentDispatchCompletedSchema,
  AgentDispatchAsyncLaunchedSchema
]);

export type AgentDispatchResult = z.infer<typeof AgentDispatchResultSchema>;
